/**
 * MobileSheetChrome — View (SE-5081)
 *
 * Pure presentation for the shared mobile bottom-sheet chrome (rounded-top
 * dark surface, top-left ✕ close chip, optional centered drag handle, optional
 * title, scrollable body, optional sticky footer).
 *
 * All logic (ESC handling, swipe-down threshold, isWeb gating) is owned by
 * MobileSheetChromeContainer. The View receives the already-bound gesture
 * callback as `handleSwipeDown`; the Container is responsible for gating it
 * with `ifWeb` so web receives no gesture handler.
 *
 * Consumers do NOT pass testIDs — the 5 chrome anchors
 * (TEST_ID_MOBILE_SHEET_CHROME.{ROOT,CLOSE,HANDLE,BODY,FOOTER}) are hard-coded
 * here so Playwright queries are stable across surfaces.
 * @module components/MobileSheetChrome
 */

import { X } from "lucide-react-native";
import { memo, type ReactNode } from "react";
import {
  Platform,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from "react-native";
import {
  GestureEvent,
  PanGestureHandler,
  PanGestureHandlerEventPayload,
} from "react-native-gesture-handler";

import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
} from "@/components/atoms/Actionsheet";
import { Box } from "@/components/atoms/Box";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_MOBILE_SHEET_CHROME } from "@/e2e/testId";

/**
 * Props for MobileSheetChromeView.
 *
 * Internal to the component — consumers use the Container's public
 * MobileSheetChromeProps (re-exported from the barrel).
 */
export interface MobileSheetChromeViewProps {
  /** Whether the sheet is open. */
  readonly isOpen: boolean;
  /** Dismissal handler (✕ tap, backdrop tap, swipe-down, ESC, Android back). */
  readonly onClose: () => void;
  /** Already-bound gesture callback (or undefined on web — gated in Container). */
  readonly handleSwipeDown:
    | ((evt: GestureEvent<PanGestureHandlerEventPayload>) => void)
    | undefined;
  /**
   * Body scroll handler — drives pull-to-dismiss (overscroll past the top).
   * Cross-platform (web + native); see the Container's overscroll threshold.
   */
  readonly handleScroll: (evt: NativeSyntheticEvent<NativeScrollEvent>) => void;
  /**
   * Whether the device carries a system-navigation bar the sticky footer needs
   * extra breathing room above (Android edge-to-edge; false on iOS/web and on
   * Android devices with gesture navigation). Supplied by the Container, which
   * owns the `useBottomSheetInset` read.
   *
   * A primitive presence flag, NOT a measurement: the padding it selects is a
   * fixed token either way ({@link FOOTER_NAV_CLEARANCE_CLASS} vs
   * {@link FOOTER_BASE_BOTTOM_CLASS}), because the nav-bar inset itself is
   * applied one layer up on `ActionsheetContent`. Stacking a second copy of
   * that inset here is what SE-6995 measured as a 114dp empty band below the
   * buttons.
   */
  readonly hasFooterNavClearance: boolean;
  /** Optional sheet title rendered below the ✕ chip. */
  readonly title?: string;
  /**
   * Optional one-line explanation under the title.
   *
   * For a sheet whose purpose is not obvious from its title alone — the
   * add-player sheet says what searching will and will not find. Wraps rather
   * than truncating; the title is the line that must stay single.
   */
  readonly subtitle?: string;
  /** Render the 4×64 drag handle centered at the top. */
  readonly showHandle: boolean;
  /**
   * Optional node rendered in the sticky header band, below the title (e.g. a
   * search bar). Stays pinned above the scrolling body.
   */
  readonly headerContent?: ReactNode;
  /**
   * Render the hairline divider above the sticky footer. Defaults to true
   * (the pre-SE-5401 behavior); consumers like the All-lists sheet opt out
   * when the design wants no divider (SE-5401: the Figma "All lists" sheet
   * has no dividing line in the sheet sector).
   */
  readonly showFooterDivider: boolean;
  /** Sheet body. */
  readonly children: ReactNode;
  /** Optional sticky footer slot. */
  readonly footer?: ReactNode;
}

/**
 * Inline style for the upward drop shadow (Figma: 0 -3px 10px #26262633).
 * No existing `shadow-*` token has an upward y-offset — `shadow-hard-5` is
 * downward — so this is one of two flagged inline-style escape hatches in
 * the chrome.
 */
const UPWARD_SHADOW_STYLE = {
  shadowColor: "#262626",
  shadowOpacity: 0.2,
  shadowOffset: { width: 0, height: -3 },
  shadowRadius: 10,
  elevation: 10,
} as const;

/**
 * Inline background for the ✕ close chip (Figma: rgba(116,116,116,0.25)).
 * NativeWind 4 arbitrary-value classes for rgba backgrounds are flaky on
 * native — inline style is the safest choice. Flagged escape hatch #2.
 */
const CLOSE_CHIP_BG_STYLE = {
  backgroundColor: "rgba(116,116,116,0.25)",
} as const;

/**
 * Bounds the sheet at 4/5 (80%) of the viewport height so the rendered
 * ActionsheetContent never grows taller than the screen. Without this cap
 * tall children make `ActionsheetContent` exceed viewport height — the
 * motion wrapper then translates the entire sheet up by (height − vh) and
 * the chrome (handle, close chip, title) scrolls off the top of the
 * viewport before the inner ScrollView can ever activate. Empirically
 * verified during SE-5081 verification round 1.
 *
 * Uses `max-h-4/5` (closed fraction) in place of the former arbitrary-value
 * 85vh class per the design-system styling migration (SE-5304). The 5%
 * difference (80% vs 85%) is snap-table-traceable and does not affect the
 * regression the cap was added for.
 *
 * WEB-ONLY as of SE-5580: on native the cap is not inert — the Content's
 * parent chain (the creator's definite-height Animated.View inside the RN
 * Modal) is definite, so the cap resolves and caps the Content at 80% of
 * the screen while the inner column keeps its own fixed height at 85% of
 * the screen. Yoga does not clip overflow, so the column's bottom margin
 * beyond the cap — i.e. the sticky footer — rendered past the Content's
 * bottom edge, permanently off-screen (and the flexible body, sized by the
 * 85%-height column, never scrolled). Empirically measured on an iPhone 17
 * Pro simulator: sheet top at ~80% cap, footer divider flush with the
 * screen edge, buttons below it. The gluestack creator bottom-anchors the
 * sheet by its MEASURED height (wrapper height minus content height), and
 * the native column keeps its own fixed 85%-of-screen height, so an
 * uncapped hugging Content (85% height + chrome padding under 100% of the
 * screen on every device) sits flush with the screen bottom with the
 * footer visible — the pre-SE-5423 unbounded-children regression this cap
 * guarded against can no longer occur on native. Web keeps the cap
 * (harmless there: the Content is pinned to the same 80% height via
 * {@link SHEET_WEB_HEIGHT_CLASS}).
 *
 * Exported so unit tests can lock the regression without re-deriving the
 * class.
 */
export const SHEET_MAX_HEIGHT_CLASS = "web:max-h-4/5";

/**
 * Definite sheet height on WEB only (SE-5423). `MainView` now gates the mobile
 * layout on screen WIDTH (`isSmallScreen`, ≤1280px) rather than `IS_WEB`, so
 * this native-built sheet now also mounts on narrow web. On web the gluestack
 * `ActionsheetContent` is content-height (only `max-h-4/5` caps it), so the
 * `flex-1` body `ScrollView` has no free space to grow into and collapses to
 * 0px — the chrome (✕ + title) renders but the list body does not. Pinning a
 * definite height on web (same closed `4/5` fraction as the max-height cap, so
 * height == the cap) gives the flex column a resolvable height; paired with
 * `web:min-h-0` on the flex chain, the body resolves and scrolls. Native is
 * untouched (`web:` variant only), preserving the SE-5081 tuning.
 *
 * Exported so unit tests can lock the regression without re-deriving the class.
 */
export const SHEET_WEB_HEIGHT_CLASS = "web:h-4/5";

/**
 * Definite sheet height on NATIVE (SE-5423). Applied to the inner flex column
 * (`Stack`), a plain cross-platform View that DOES resolve a height on native —
 * unlike the motion-based `ActionsheetContent`, which honors neither a height
 * class nor an inline pixel `height` and instead hugs its child (empirically
 * verified on a Galaxy S24 / Android 16 build: with the height on the Content,
 * the sheet ROOT stayed 171px, the body 0px, and the list rendered empty).
 * Because the Content hugs its child, a definite height on the column forces the
 * Content to match; the `flex-1` body `ScrollView` then resolves and scrolls.
 *
 * Uses `native:h-[85vh]` (viewport-relative) rather than the `4/5` fraction the
 * web path uses: `h-4/5` is PARENT-relative, and on native the
 * `ActionsheetContent` parent has no definite height, so a fraction resolves to
 * nothing. `vh` is viewport-relative and resolves regardless of parent (same
 * mechanism as the native `max-h-[40vh]` on `ManageActionSheetView`). Scoped
 * `native:` so web is untouched (web pins the height on the Content via
 * {@link SHEET_WEB_HEIGHT_CLASS}). Without it the body collapses to 0px on
 * native and the list renders empty.
 *
 * Exported so unit tests can lock the regression without re-deriving the class.
 */
export const SHEET_NATIVE_HEIGHT_CLASS = "native:h-[85vh]";

/**
 * Top-corner radius for the bottom sheet (SE-5401). The vendored
 * `ActionsheetContent` base ships `rounded-tl-lg rounded-tr-lg` (12px); the
 * Figma "All lists" sheet wants more rounded top curves, so the chrome
 * overrides both top corners with the closed `xl`(20px) radius token. Applied
 * here (not in the shared Actionsheet atom) so the larger curve is scoped to
 * the mobile bottom-sheet chrome — the foundation for all mobile bottom
 * modals — rather than every Actionsheet (selects, etc.) app-wide.
 *
 * Exported so unit tests can lock the regression without re-deriving the
 * class.
 */
export const SHEET_TOP_RADIUS_CLASS = "rounded-tl-xl rounded-tr-xl";

/**
 * Foreground (icon + text) class on a `bg-surface-base` surface. In this
 * codebase the `typography-0` and `typography-950` scales **invert**
 * between light and dark mode (`config.ts` lines 414/425/572/583):
 * `typography-0` is the lightest tone in light mode but the darkest in
 * dark mode. The contrast-correct token for "always-readable text on
 * `bg-surface-base`" is `typography-950`, not `typography-0` — the latter
 * renders black-on-dark in dark mode.
 *
 * Exported so unit tests can lock this regression.
 */
export const DARK_SURFACE_FOREGROUND_CLASS = "text-surface-inverse";

/**
 * Sticky-footer bottom padding on a device WITH a system-navigation bar —
 * `pb-4`, the 16px `space-4` token (SE-5441 breathing room, so bottom-pinned
 * content such as "+ Create new list" isn't cramped against the sheet edge).
 *
 * This is breathing room ONLY. It does not carry the nav-bar inset:
 * `ActionsheetContent` already pads the whole sheet by the captured inset via
 * `useBottomSheetInsetStyle` (SE-5151), so total clearance is `inset + 16`.
 * Turning this back into a device-measured value re-creates the SE-6995 stack.
 *
 * Exported so unit tests can lock the regression without re-deriving the class.
 */
export const FOOTER_NAV_CLEARANCE_CLASS = "pb-4";

/**
 * Sticky-footer bottom padding with NO system-navigation bar (iOS, web, and
 * gesture-nav Android) — `pb-3`, the bottom half of the footer's own `py-3`.
 *
 * Spelled out as a discrete `pb-*` rather than leaving `py-3` to be overridden,
 * so exactly one bottom-padding utility is ever emitted and the two cases
 * cannot collide in the stylesheet cascade.
 *
 * Exported so unit tests can lock the regression without re-deriving the class.
 */
export const FOOTER_BASE_BOTTOM_CLASS = "pb-3";

/**
 * Pure presentation for the shared mobile bottom-sheet chrome.
 * @param props - Component props (see MobileSheetChromeViewProps)
 * @param props.isOpen - Whether the sheet is currently open
 * @param props.onClose - Dismissal handler bound by the Container
 * @param props.handleSwipeDown - Already-gated swipe-down callback (undefined on web)
 * @param props.handleScroll - Body scroll handler driving pull-to-dismiss
 * @param props.hasFooterNavClearance - Whether the device has a system nav bar the footer needs breathing room above
 * @param props.title - Optional sheet title rendered below the ✕ chip
 * @param props.subtitle - Optional one-line explanation under the title
 * @param props.showHandle - Whether to render the 4×64 drag handle
 * @param props.headerContent - Optional sticky header node below the title (e.g. search bar)
 * @param props.showFooterDivider - Whether to draw the hairline above the footer
 * @param props.children - Sheet body rendered inside the ScrollView
 * @param props.footer - Optional sticky footer node
 */
const MobileSheetChromeView = ({
  isOpen,
  onClose,
  handleSwipeDown,
  handleScroll,
  hasFooterNavClearance,
  title,
  subtitle,
  showHandle,
  headerContent,
  showFooterDivider,
  children,
  footer,
}: MobileSheetChromeViewProps) => (
  <Actionsheet
    isOpen={isOpen}
    onClose={onClose}
    useRNModal={Platform.OS !== "web"}
  >
    <ActionsheetBackdrop />
    <ActionsheetContent
      testID={TEST_ID_MOBILE_SHEET_CHROME.ROOT}
      className={`${SHEET_TOP_RADIUS_CLASS} ${SHEET_MAX_HEIGHT_CLASS} ${SHEET_WEB_HEIGHT_CLASS} p-0 pt-2`}
      // eslint-disable-next-line no-restricted-syntax -- upward drop shadow (0 -3px 10px #262626); no shadow-* token has a negative-y offset and ActionsheetContent is a bare re-export atom with no UNSAFE_style prop, so style= must stay
      style={UPWARD_SHADOW_STYLE}
    >
      <PanGestureHandler onGestureEvent={handleSwipeDown}>
        {/* The definite height lives here on native (Stack = plain View, via
         * SHEET_NATIVE_HEIGHT_CLASS), on the ActionsheetContent on web. `flex-1`
         * is web-only: on native an explicit height must NOT also be a flex item
         * with basis-0, or yoga's flex-basis wins and the height is ignored
         * (the collapse this fixes). Web keeps flex-1 to fill the Content's
         * web:h-4/5. */}
        <Stack
          className={`${SHEET_NATIVE_HEIGHT_CLASS} web:min-h-0 web:flex-1 w-full`}
        >
          {/* Sticky header band.
           *
           * Structural note: the close chip, title, and optional header
           * content (e.g. search bar) sit in normal flex flow (NOT absolute)
           * so they don't lose the stacking-context fight against the
           * ScrollView body below. The handle stays absolute &
           * `pointer-events-none` so it can be centered horizontally
           * without intercepting taps. Verifier round 1 caught the prior
           * "absolute HStack" pattern letting the body subtree intercept
           * pointer events on the bottom 20px of the chip.
           *
           * SE-5401: the ✕ close chip now sits ABOVE the title (its own row)
           * rather than beside it, matching the Figma "All lists" sheet.
           */}
          <Box className="relative w-full px-3 pb-2 pt-3">
            {showHandle ? (
              <Box className="absolute inset-x-0 top-3 items-center pointer-events-none">
                {/*
                 * Raw RN View — the cross-platform Box renders as an HTML
                 * div on web, which rejects the `testID` prop type. Using
                 * View directly matches the SeeAllListsSectionView
                 * precedent. NativeWind still applies the class.
                 */}
                <Box
                  testID={TEST_ID_MOBILE_SHEET_CHROME.HANDLE}
                  className="h-1 w-16 rounded-full bg-surface-strong"
                />
              </Box>
            ) : null}

            <Stack className="gap-3">
              <Pressable
                testID={TEST_ID_MOBILE_SHEET_CHROME.CLOSE}
                accessibilityRole="button"
                accessibilityLabel="Close"
                onPress={onClose}
                className="size-8 items-center justify-center rounded-full"
                // eslint-disable-next-line no-restricted-syntax -- runtime rgba close-chip background rgba(116,116,116,0.25); no semantic token exists and NativeWind 4 arbitrary rgba classes are flaky on native
                UNSAFE_style={CLOSE_CHIP_BG_STYLE}
              >
                <Icon
                  as={X}
                  size="sm"
                  className={DARK_SURFACE_FOREGROUND_CLASS}
                />
              </Pressable>

              {title ? (
                /*
                 * SE-7709: `w-full` states a width this title already had. It is
                 * a direct child of the enclosing `<Stack className="gap-3">`
                 * (VStack, items-stretch), inside the
                 * `<Box className="relative w-full …">` — so the width comes from
                 * `w-full` above, not from intrinsic measurement, and the Android
                 * font-undercount clip cannot occur. (Grep those two ancestors
                 * rather than trusting a line number: ~L303/L287 at time of
                 * writing. A shifted line is cosmetic; a missing `relative
                 * w-full` Box means this reasoning has expired.)
                 * Measured rather than reasoned: at 360px the element and its
                 * parent content box are both 334px while the text's natural
                 * width is 74.73px, and adding `w-full` moved nothing (element,
                 * parent and every sibling rect byte-identical).
                 */
                <Text
                  variant="title-sm"
                  numberOfLines={1}
                  className={`w-full ${DARK_SURFACE_FOREGROUND_CLASS}`}
                >
                  {title}
                </Text>
              ) : null}

              {/* One line under the title, when a sheet has something to say
                  about WHY it opened (SE-7550). `numberOfLines` is deliberately
                  absent: this wraps, because a truncated explanation is worse
                  than a two-line one — the title above is the part that must
                  stay on one line. */}
              {subtitle ? (
                <Text
                  variant="caption"
                  className={`w-full ${DARK_SURFACE_FOREGROUND_CLASS}`}
                >
                  {subtitle}
                </Text>
              ) : null}

              {headerContent ?? null}
            </Stack>
          </Box>

          {/* Scrolling body */}
          <ScrollView
            testID={TEST_ID_MOBILE_SHEET_CHROME.BODY}
            className="web:min-h-0 w-full flex-1"
            contentContainerClassName="px-4 pt-3 pb-4"
            keyboardShouldPersistTaps="handled"
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            {children}
          </ScrollView>

          {/* Sticky footer — raw RN View for the same testID-on-web reason
           * documented above. */}
          {footer ? (
            <Box
              testID={TEST_ID_MOBILE_SHEET_CHROME.FOOTER}
              className={`w-full px-4 pt-3 ${
                hasFooterNavClearance
                  ? FOOTER_NAV_CLEARANCE_CLASS
                  : FOOTER_BASE_BOTTOM_CLASS
              } ${showFooterDivider ? "border-t border-outline-subtle" : ""}`}
            >
              {footer}
            </Box>
          ) : null}
        </Stack>
      </PanGestureHandler>
    </ActionsheetContent>
  </Actionsheet>
);

MobileSheetChromeView.displayName = "MobileSheetChromeView";

export default memo(MobileSheetChromeView);
